import { format, isValid, parse } from "date-fns";

import { GoalBasedFinancialPlanExpenseResponse } from "@fartherfinance/frontend/api/FinancialPlans/expenses/types";
import { GoalBasedFinancialPlanGoalResponse } from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import { GoalBasedFinancialPlanIncomeResponse } from "@fartherfinance/frontend/api/FinancialPlans/income/types";
import { GoalBasedFinancialPlanRetirementHorizonResponse } from "@fartherfinance/frontend/api/FinancialPlans/retirementHorizon/types";
import { GoalBasedFinancialPlanSavingsResponse } from "@fartherfinance/frontend/api/FinancialPlans/savings/types";
import {
  GoalBasedPlanDateResponse,
  GoalBasedPlanDateType,
  GoalBasedPlanDateTypeSchema,
  GoalBasedRepeatTimeFrame,
} from "@fartherfinance/frontend/api/FinancialPlans/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import {
  AlreadyStartedOption,
  CalendarYearEndField,
  CalendarYearOption,
  CalendarYearStartField,
  defaultPlanDateFieldValues,
  defaultRepeatsFieldOption,
  EndsField,
  RepeatsField,
  repeatTimeFrameOptions,
  SpousesAgeEndField,
  SpousesAgeStartField,
  StartsField,
  TargetDateField,
  YourAgeEndField,
  YourAgeStartField,
} from "../constants";
import { RepeatDropdownFormFieldTypes } from "../Types";

import { convertAgeToDate } from "./convertAgeToDate";
import { getOptionForStartEndPlanDateField } from "./getOptionForStartEndPlanDateField";

const getTargetDateField = (
  repeatType: GoalBasedRepeatTimeFrame | undefined,
  dateType: GoalBasedPlanDateType | undefined,
  value: string | undefined,
  dateOfBirth: string | undefined
): Date | undefined => {
  if (value === undefined || repeatType !== "None") {
    return undefined;
  }
  if (dateType === "Age" && dateOfBirth !== undefined) {
    return convertAgeToDate({
      value,
      dateOfBirth,
    });
  }

  if (!isValid(parse(value, "yyyy-MM-dd", new Date()))) {
    return undefined;
  }

  return parse(value, "yyyy-MM-dd", new Date());
};

interface Params {
  clientId: ClientId;
  retirementHorizons: GoalBasedFinancialPlanRetirementHorizonResponse[];
  existingCategoryElement:
    | GoalBasedFinancialPlanGoalResponse
    | GoalBasedFinancialPlanExpenseResponse
    | GoalBasedFinancialPlanIncomeResponse
    | GoalBasedFinancialPlanSavingsResponse
    | undefined;
  dateOfBirth?: string;
}

export const getInitialPlanDateFieldValuesForForm = ({
  clientId,
  retirementHorizons,
  existingCategoryElement,
  dateOfBirth,
}: Params): RepeatDropdownFormFieldTypes => {
  const { Age, DateString, AlreadyStarted, Year } =
    GoalBasedPlanDateTypeSchema.Enum;

  if (
    existingCategoryElement === undefined ||
    existingCategoryElement === null
  ) {
    return { ...defaultPlanDateFieldValues };
  }

  const { startDate, owner, endDate } = existingCategoryElement;

  const repeatsField = repeatTimeFrameOptions.find(
    (el) => el.value === existingCategoryElement.repeats
  ) ?? { ...defaultRepeatsFieldOption };

  const targetDateField = getTargetDateField(
    existingCategoryElement.repeats,
    startDate?.dateType,
    startDate?.value,
    dateOfBirth
  );

  const startFieldOption =
    getOptionForStartEndPlanDateField(
      startDate === null ? undefined : startDate,
      owner,
      clientId,
      retirementHorizons
    ) ?? AlreadyStartedOption;

  const startsField = startFieldOption;

  const endsFieldOption =
    getOptionForStartEndPlanDateField(
      endDate === null ? undefined : endDate,
      owner,
      clientId,
      retirementHorizons
    ) ?? CalendarYearOption;

  const getCalendarYearFieldValue = (
    date: GoalBasedPlanDateResponse | undefined | null
  ) => {
    if (date === undefined || date === null) {
      return undefined;
    }
    switch (date.dateType) {
      case Year:
        return date.value;
      case DateString:
        return format(new Date(date.value.replace(/-/g, "/")), "yyyy");
      default:
        return undefined;
    }
  };

  const endsField =
    endsFieldOption.value === AlreadyStarted
      ? CalendarYearOption
      : endsFieldOption;

  const yourAgeStartField =
    clientId === owner && startDate?.dateType === Age
      ? startDate.value
      : undefined;

  const yourAgeEndField =
    clientId === owner && endDate?.dateType === Age ? endDate.value : undefined;

  const spousesAgeEndField =
    clientId !== owner && endDate?.dateType === Age ? endDate.value : undefined;

  const spousesAgeStartField =
    clientId !== owner && startDate?.dateType === Age
      ? startDate.value
      : undefined;

  return {
    [RepeatsField]: repeatsField,
    [TargetDateField]: targetDateField,
    [StartsField]: startsField,
    [EndsField]: endsField,
    [CalendarYearStartField]: getCalendarYearFieldValue(startDate),
    [YourAgeStartField]: yourAgeStartField,
    [SpousesAgeStartField]: spousesAgeStartField,
    [CalendarYearEndField]: getCalendarYearFieldValue(endDate),
    [YourAgeEndField]: yourAgeEndField,
    [SpousesAgeEndField]: spousesAgeEndField,
  };
};
